import { addDays, endOfDay, format, isDate, isValid, startOfDay } from 'date-fns'
import { ActiveFilterDisplay, FilterMeta, QueryParam } from './common'
import { dateOnlyFormat, dateTimeFormat, est, formatAsUTC, monthOnlyFormat, utc } from '../../utilities/date'
import { cond, equals } from 'lodash/fp'
import { FilterOperation, FilterParams } from '../../utilities/http-params'
import { constant } from 'lodash'
import { ServicePointGroupWithRefs } from 'src/app/public/service-point-groups/service-point-group.types'
import { TZDate } from '@date-fns/tz'

export type DateOperation =
  | 'lt'
  | 'lte'
  | 'eq'
  | 'gte'
  | 'gt'
  | 'between'

export const DateOperation = {
  lt: 'lt',
  lte: 'lte',
  eq: 'eq',
  gte: 'gte',
  gt: 'gt',
  between: 'between',
} as const

export const DateOperationDisplay: Record<DateOperation, string> = {
  'lte': '≤',
  'lt': '<',
  'eq': '=',
  'gt': '>',
  'gte':'≥',
  'between':'Between',
}

export const DateOperationQueryPrefix: Record<DateOperation, FilterOperation> = {
  'lte': '<=',
  'lt': '<',
  'eq': '==',
  'gt': '>',
  'gte':'>=',
  'between':'between=',
}


export type DateRangeFilterStateValue =
  | { operation: null, value: null }
  | { operation: 'between', value: [Date, Date] }
  | { operation: Exclude<DateOperation, 'between'>, value: Date }

export type DateRangeFilterState = DateRangeFilterStateValue & Readonly<{
  config: {
    meta: FilterMeta
    sendTime?: boolean
    showTime?: boolean
    showSeconds?: boolean
    showOperation?: boolean
    showRange?: boolean
    onlyMonth?: boolean
    viewAsTZ?: string
    constant?: boolean
  }
}>

export const initialDateRangeFilterState: DateRangeFilterState = {
  operation: null,
  value: null,
  config: {
    meta: {
      fieldName: '',
      fieldGroup: '',
      fieldDisplay: '',
    },
  },
}

export interface SetDateRangeFilterConfigAction { 
  type: 'SET_DATE_RANGE_FILTER_CONFIG_ACTION', 
  payload: Pick<DateRangeFilterState, 'config'>, 
  meta: Omit<DateRangeFilterState['config']['meta'], 'fieldDisplay'>
}

export interface SetDateRangeFilterValueAction { 
  type: 'SET_DATE_RANGE_FILTER_VALUE_ACTION', 
  payload: DateRangeFilterStateValue, 
  meta: Omit<DateRangeFilterState['config']['meta'], 'fieldDisplay'>
}

export type DateRangeFilterAction =
  | SetDateRangeFilterConfigAction
  | SetDateRangeFilterValueAction

export const dateRangeFilterReducer = (state: DateRangeFilterState, action: DateRangeFilterAction): DateRangeFilterState => {
  switch(action.type) {
    case 'SET_DATE_RANGE_FILTER_CONFIG_ACTION':
      return {
        ...state,
        config: action.payload.config,
      }
    case 'SET_DATE_RANGE_FILTER_VALUE_ACTION':
      return {
        ...state,
        ...action.payload,
      }
    default:
      return state
  }
}


export class DateRangeFilter {
  readonly type = 'DATE_RANGE'

  private readonly queryParamFormat = `${dateOnlyFormat}'T'HH:mm:ss`
  private readonly queryParamDateFormat = dateOnlyFormat
  private readonly dashboardParamFormat = `${this.queryParamFormat}.SSS`

  static SetDateRangeFilterConfigAction(
    payload: SetDateRangeFilterConfigAction['payload'], 
    meta: Omit<DateRangeFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetDateRangeFilterConfigAction {
    return { type: 'SET_DATE_RANGE_FILTER_CONFIG_ACTION', payload, meta }
  }

  static SetDateRangeFilterValueAction(
    payload: SetDateRangeFilterValueAction['payload'], 
    meta: Omit<DateRangeFilterState['config']['meta'], 'fieldDisplay'>,
  ): SetDateRangeFilterValueAction {
    return { type: 'SET_DATE_RANGE_FILTER_VALUE_ACTION', payload, meta }
  }

  state: DateRangeFilterState

  constructor(
    partialStateWithConfig: { config: DateRangeFilterState['config'] } | DateRangeFilterState,
    private dispatch: (action: DateRangeFilterAction) => void,
  ) {
    this.state = { ...initialDateRangeFilterState ,...partialStateWithConfig } 
  }

  apply(action: DateRangeFilterAction) {
    this.state = dateRangeFilterReducer(this.state, action)
    return this
  }

  // query param should always be formatted in UTC since it's not ISO 8601 (sigh)
  private toTimestampQueryParam(value: Date): string {
    if (value instanceof TZDate && value.timeZone !== 'UTC') {
      return this.toTimestampQueryParam(new TZDate(value.valueOf(), 'UTC'))
    } else {
      return format(value, this.queryParamFormat)
    }
  }

  asParams(): FilterParams[] {
    if (this.state.operation === null) {
      return []
    }

    if(this.state.config.sendTime === false) {
      return [
        {
          name: this.state.config.meta.fieldName,
          values: [
            {
              value: [this.state.value]
                .flat()
                .map((v) => format(v, this.queryParamDateFormat))
                .join(','),
              operation: DateOperationQueryPrefix[this.state.operation],
            },
          ],
        },
      ]
    }

    if (!this.state.config.showTime && this.state.operation === DateOperation.eq) {
      // query start to end of selected day
      return [
        {
          name: this.state.config.meta.fieldName,
          values: [
            {
              value: [
                utc(startOfDay(est(this.state.value))),
                utc(endOfDay(est(this.state.value))),
              ]
                .flat()
                .map((v) => format(v, this.queryParamFormat))
                .join(','),
              operation: DateOperationQueryPrefix[DateOperation.between],
            },
          ],
        },
      ]
    }

    return [
      {
        name: this.state.config.meta.fieldName,
        values: [
          {
            value: [this.state.value]
              .flat()
              .map((v) => this.toTimestampQueryParam(v))
              .join(','),
            operation: DateOperationQueryPrefix[this.state.operation],
          },
        ],
      },
    ]
  }

  asActiveDisplay(): ActiveFilterDisplay[] {
    const displayFormat = this.state.config.onlyMonth 
      ? monthOnlyFormat
      : this.state.config.showTime ? dateTimeFormat : dateOnlyFormat
    return this.state.operation !== null 
      ? [
        {
          headerName: this.state.config.meta.fieldDisplay,
          operation: (DateOperationDisplay)[this.state.operation],
          value: [this.state.value]
            .flat()
            .map((v) => est(v))
            .map((v) => format(v, displayFormat))
            .join(' & '),
          constant: this.state.config.constant ?? false,
          remove: (() => this.dispatch(DateRangeFilter.SetDateRangeFilterValueAction(
            {
              value: null,
              operation: null,
            },
            this.state.config.meta,
          ))),
        },
      ] 
      : []
  }

  asCount() {
    return this.state.value !== null ? 1 : 0
  }

  fromUrlQueryParams(
    params: readonly QueryParam[],
    defaultVal: Date[] | Date | string | number | null | undefined,
  ): void {
    const qp = params.find(p => p.name === this.state.config.meta.fieldName)
    if(qp?.value) {
      const operation: DateOperation | undefined = Object.values(DateOperation)
        .sort()    // order matters her because we
        .reverse() // want to check for lte before lt, etc.
        .find((op) => qp.value.startsWith(op))
      const value = qp.value.slice(operation?.length ?? 0).split('~').map(utc).filter(isValid)
      if(operation === 'between' && value.length >= 1) {
        this.dispatch(DateRangeFilter.SetDateRangeFilterValueAction(
          { operation, value: [value[0], value[1] ?? value[0]] },
          this.state.config.meta,
        ))
      }
      else if(operation != 'between' && value.length >= 1) {
        this.dispatch(DateRangeFilter.SetDateRangeFilterValueAction(
          { operation: operation ?? 'eq', value: value[0] },
          this.state.config.meta,
        ))
      } else {
        // eslint-disable-next-line no-console
        console.warn(`${this.state.config.meta.fieldName} query param could not be parsed: Not enough dates`)
      }
    }
    else if (defaultVal && typeof defaultVal === 'number') {
      this.dispatch(DateRangeFilter.SetDateRangeFilterValueAction(
        { operation: 'eq', value: addDays(startOfDay(utc()), defaultVal) },
        this.state.config.meta,
      ))
    }
    else if (defaultVal && typeof defaultVal === 'string') {
      this.dispatch(DateRangeFilter.SetDateRangeFilterValueAction(
        { operation: 'eq', value: startOfDay(utc(defaultVal)) },
        this.state.config.meta,
      ))
    }
    else if (defaultVal && Array.isArray(defaultVal)) {
      if(defaultVal.length >= 1) {
        this.dispatch(DateRangeFilter.SetDateRangeFilterValueAction(
          { operation: 'between', value: [defaultVal[0], defaultVal[1] ?? defaultVal[0]] },
          this.state.config.meta,
        ))
      }
      else {
        // eslint-disable-next-line no-console
        console.assert(false, `${this.state.config.meta.fieldName} default date range could not be parsed: Not enough dates`)
      }
    }
    else if (defaultVal && isDate(defaultVal)) {
      this.dispatch(DateRangeFilter.SetDateRangeFilterValueAction(
        { operation: 'eq', value: utc(defaultVal) },
        this.state.config.meta,
      ))
    }
  }

  asUrlQueryParams(): QueryParam[] {
    return this.state.operation !== null 
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: `${this.state.operation}${[this.state.value]
            .flat()
            .map((v) => formatAsUTC(v))
            .join('~')
          }`,
        },
      ] 
      : []
  }

  asDashboardQueryParams(): QueryParam[] {
    const values: Date[] | null = cond([
      [equals(null), constant(null)],
      [
        equals('lt'),
        (): Date[] => [
          est('2007-01-01T05:00:00Z'),
          this.state.config.showTime
            ? this.state.value as Date
            : startOfDay(est(this.state.value as Date)),
        ],
      ],
      [
        equals('lte'),
        (): Date[] => [
          est('2007-01-01T05:00:00Z'),
          this.state.config.showTime
            ? this.state.value as Date
            : endOfDay(est(this.state.value as Date)),
        ],
      ],
      [
        equals('gt'),
        (): Date[] => [
          this.state.config.showTime
            ? this.state.value as Date
            : endOfDay(est(this.state.value as Date)),
          endOfDay(est()),
        ],
      ],
      [
        equals('gte'),
        (): Date[] => [
          this.state.config.showTime
            ? this.state.value as Date
            : startOfDay(est(this.state.value as Date)),
          endOfDay(est()),
        ],
      ],
      [
        equals('eq'),
        (): Date[] => [
          startOfDay(est(this.state.value as Date)),
          endOfDay(est(this.state.value as Date)),
        ],
      ],
      [
        equals('between'),
        (): Date[] =>
          this.state.config.showTime
            ? this.state.value as [Date, Date]
            : [
              startOfDay(est((this.state.value as Date[])[0])),
              endOfDay(est((this.state.value as Date[])[1])),
            ],
      ],
    ])(this.state.operation)

    return this.state.operation !== null 
      ? [
        {
          name: this.state.config.meta.fieldName,
          value: values!
            .map((v) => encodeURIComponent(format(est(v!), this.dashboardParamFormat)))
            .join('~'),
        },
      ]
      : []
  }

  asServicePointGroupWithRefs(): Partial<ServicePointGroupWithRefs> {
    return {}
  }
}
